import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { observer } from 'mobx-react-lite';
import { userStore } from '../../store';
import { Loader, showToast } from '../../components/shared';
import { ArrowLeft, Lock, Eye, EyeOff, Shield } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

const ChangePasswordScreen: React.FC = observer(() => {
  const navigate = useNavigate();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [saving, setSaving] = useState(false);

  const handleSubmit = () => {
    if (!currentPassword.trim()) {
      showToast('Please enter current password', 'error');
      return;
    }
    if (newPassword.length < 7) {
      showToast('New password must be at least 7 characters', 'error');
      return;
    }
    if (newPassword !== confirmPassword) {
      showToast('Passwords do not match', 'error');
      return;
    }

    setSaving(true);
    userStore.changePassword(currentPassword, newPassword, (success, message) => {
      setSaving(false);
      if (success) {
        showToast(message || 'Password changed!', 'success');
        navigate(-1);
      } else {
        showToast(message || 'Failed to change password', 'error');
      }
    });
  };

  const PasswordInput = ({
    label,
    value,
    onChange,
    placeholder,
    show,
    onToggle,
  }: {
    label: string;
    value: string;
    onChange: (val: string) => void;
    placeholder: string;
    show: boolean;
    onToggle: () => void;
  }) => (
    <div className="space-y-2">
      <Label>{label}</Label>
      <div className="relative">
        <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
        <Input
          type={show ? 'text' : 'password'}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          className="pl-10 pr-10 h-12"
        />
        <button onClick={onToggle} className="absolute right-3 top-1/2 -translate-y-1/2" type="button">
          {show ? (
            <EyeOff className="w-4 h-4 text-muted-foreground" />
          ) : (
            <Eye className="w-4 h-4 text-muted-foreground" />
          )}
        </button>
      </div>
    </div>
  );

  return (
    <div className="h-full flex flex-col bg-background">
      <Loader visible={saving} message="Updating password..." />

      {/* Header */}
      <div className="safe-area-top bg-background border-b border-border">
        <div className="flex items-center px-4 py-3">
          <button
            onClick={() => navigate(-1)}
            className="w-10 h-10 flex items-center justify-center rounded-full hover:bg-muted transition-colors"
          >
            <ArrowLeft className="w-5 h-5 text-foreground" />
          </button>
          <h1 className="flex-1 text-center text-lg font-semibold text-foreground pr-10">
            Change Password
          </h1>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto">
        <div className="px-6 pt-8 pb-6">
          <div className="flex flex-col items-center mb-8">
            <div className="w-16 h-16 bg-secondary/10 rounded-full flex items-center justify-center mb-3">
              <Shield className="w-7 h-7 text-secondary" />
            </div>
            <p className="text-sm text-muted-foreground text-center">
              Password must be at least 7 characters long
            </p>
          </div>

          <div className="space-y-4">
            <PasswordInput
              label="Current Password"
              value={currentPassword}
              onChange={setCurrentPassword}
              placeholder="Enter current password"
              show={showCurrent}
              onToggle={() => setShowCurrent(!showCurrent)}
            />
            <PasswordInput
              label="New Password"
              value={newPassword}
              onChange={setNewPassword}
              placeholder="Enter new password"
              show={showNew}
              onToggle={() => setShowNew(!showNew)}
            />
            <PasswordInput
              label="Confirm New Password"
              value={confirmPassword}
              onChange={setConfirmPassword}
              placeholder="Confirm new password"
              show={showConfirm}
              onToggle={() => setShowConfirm(!showConfirm)}
            />

            <Button
              onClick={handleSubmit}
              className="w-full h-12 text-base font-semibold mt-2 bg-secondary text-secondary-foreground hover:bg-secondary/90"
            >
              Update Password
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
});

export default ChangePasswordScreen;
